import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService, DismissedPopupKeywords, Kindergarten, KindergartenContract, Teacher, User } from '@isophi/core-legacy';
import { ListResponseContract, TeacherContract } from '@isophi/teachers-shared';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map, share, shareReplay, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

/**
 * Combines kindergarten management, teacher listing and workplace (selection) logic.
 * Provides the current kindergarten, handles persistence, and fetches data from API.
 */
@Injectable({
  providedIn: 'root',
})
export class KindergartenService {
  public loggedKindergarten: Kindergarten | null = null;

  public readonly loggedKindergarten$ = new BehaviorSubject<Kindergarten | null>(null);

  private selectedKindergartenUUID: string | null = null;

  private readonly kindergartenUuidKey = 'selectedKindergartenUUID';

  private teacherSubject$ = new BehaviorSubject<void>(null);

  constructor(private httpClient: HttpClient, private authService: AuthService) {
    this.loadSelectedKindergartenUUID();
  }

  public get teachers$(): Observable<User[]> {
    const url = `${environment.teacherAPI}/teachers/`;

    return this.teacherSubject$.pipe(
      switchMap(() =>
        this.httpClient.get<ListResponseContract<TeacherContract>>(url).pipe(
          map((response) =>
            response.results.map((teacherData) => {
              const user = User.deserialize(teacherData.user);
              Teacher.deserialize(teacherData, user);
              return user;
            })
          ),
          shareReplay(1)
        )
      ),
      share()
    );
  }

  public reloadTeachersList(): void {
    this.teacherSubject$.next();
  }

  /**
   * Download the currently logged kindergarten from the backend.
   */
  public async downloadLoggedKindergarten(): Promise<void> {
    const url = `${environment.teacherAPI}/general/logged-kindergarten/`;
    try {
      const response = await this.httpClient.get<KindergartenContract>(url).toPromise();
      const newKindergarten = Kindergarten.deserialize(response);
      this.loggedKindergarten = newKindergarten;
      this.loggedKindergarten$.next(newKindergarten);
      this.normalizeSelectedKindergartenUUID();
    } catch (err) {
      console.error('Failed to load logged kindergarten:', err);
      this.loggedKindergarten = null;
      this.loggedKindergarten$.next(null);
    }
  }

  dismissTeacherPopup(popup: DismissedPopupKeywords): Promise<any> {
    const url = `${environment.teacherAPI}/popup-dismissals/`;

    return firstValueFrom(this.httpClient.post(url, { popup_keyword: popup })).then(() => {
      this.authService.dismissUserPopup(popup);
    });
  }

  /**
   * Loads the selected kindergarten UUID from localStorage.
   */
  private loadSelectedKindergartenUUID(): void {
    const stored = localStorage.getItem(this.kindergartenUuidKey);
    this.selectedKindergartenUUID = stored;
  }

  private isSelectedKindergartenAllowed(kindergartenUuid: string | null): boolean {
    if (!kindergartenUuid) {
      return false;
    }

    const kindergartens = this.authService.loggedUser?.teacher?.kindergartens || [];
    return kindergartens.some((kindergarten) => kindergarten.uuid === kindergartenUuid);
  }

  private normalizeSelectedKindergartenUUID(): void {
    if (!this.selectedKindergartenUUID) {
      return;
    }

    if (this.authService.loggedUser && !this.isSelectedKindergartenAllowed(this.selectedKindergartenUUID)) {
      this.selectedKindergartenUUID = null;
      this.saveSelectedKindergartenUUID();
    }
  }

  /**
   * Saves the selected kindergarten UUID to localStorage.
   */
  private saveSelectedKindergartenUUID(): void {
    if (this.selectedKindergartenUUID) {
      localStorage.setItem(this.kindergartenUuidKey, this.selectedKindergartenUUID);
    } else {
      localStorage.removeItem(this.kindergartenUuidKey);
    }
  }

  /**
   * Returns the UUID of the currently active kindergarten.
   * Prefers the selected one, falls back to the logged kindergarten.
   */
  public getCurrentKindergartenUUID(): string | null {
    this.normalizeSelectedKindergartenUUID();
    return this.selectedKindergartenUUID || this.loggedKindergarten?.uuid || null;
  }

  /**
   * Returns the object of the currently active kindergarten (if available).
   */
  public getCurrentKindergarten(): Kindergarten | null {
    const uuid = this.getCurrentKindergartenUUID();
    const all = this.authService.loggedUser?.teacher?.kindergartens ?? [];
    return all.find((k) => k.uuid === uuid) ?? this.loggedKindergarten;
  }

  /**
   * Sets the selected kindergarten UUID and stores it.
   */
  public setSelectedKindergarten(kindergartenUUID: string): void {
    if (this.authService.loggedUser && !this.isSelectedKindergartenAllowed(kindergartenUUID)) {
      this.clearSelectedKindergarten();
      return;
    }

    this.selectedKindergartenUUID = kindergartenUUID;
    this.saveSelectedKindergartenUUID();
  }

  /**
   * Clears the selected kindergarten UUID from localStorage.
   */
  public clearSelectedKindergarten(): void {
    this.selectedKindergartenUUID = null;
    this.saveSelectedKindergartenUUID();
  }
}
